/* Cahvé custom search box — a fully self-contained overlay search.
   Does not use Dawn's <predictive-search>, <search-form> or their styles.
   Predictive results are fetched from the storefront predictive-search endpoint
   and rendered by sections/cahve-search-results.liquid. */
if (!customElements.get('cahve-search-box')) {
  customElements.define(
    'cahve-search-box',
    class CahveSearchBox extends HTMLElement {
      connectedCallback() {
        if (this.isReady) return;
        this.isReady = true;

        this.predictiveUrl = this.dataset.predictiveUrl || '/search/suggest';
        this.sectionId = this.dataset.sectionId || 'cahve-search-results';
        this.desktopQuery = window.matchMedia('(min-width: 990px)');
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.trigger = this.querySelector('[data-csb-trigger]');
        this.overlay = this.querySelector('[data-csb-overlay]');
        this.backdrop = this.querySelector('[data-csb-backdrop]');
        this.dialog = this.querySelector('[data-csb-dialog]');
        this.form = this.querySelector('[data-csb-form]');
        this.input = this.querySelector('[data-csb-input]');
        this.clearButton = this.querySelector('[data-csb-clear]');
        this.closeButton = this.querySelector('[data-csb-close]');
        this.body = this.querySelector('[data-csb-body]');
        this.discovery = this.querySelector('[data-csb-discovery]');
        this.results = this.querySelector('[data-csb-results]');
        this.recent = this.querySelector('[data-csb-recent]');
        this.recentList = this.querySelector('[data-csb-recent-list]');
        this.rail = this.querySelector('[data-csb-rail]');
        this.railPrev = this.querySelector('[data-csb-rail-prev]');
        this.railNext = this.querySelector('[data-csb-rail-next]');

        const chipRolloverDuration = window
          .getComputedStyle(this)
          .getPropertyValue('--cahve-menu-rollover-duration')
          .trim();
        if (chipRolloverDuration) {
          this.overlay?.style.setProperty('--csb-chip-rollover-duration', chipRolloverDuration);
        }

        // Portal the overlay to <body>. Inside the header's `.cahve-header__surface`
        // (which uses `isolation: isolate`) a fixed backdrop cannot dim sibling
        // page sections; as a direct child of <body> it covers the whole page.
        // The trigger stays in the header and still drives the overlay by ref.
        if (this.overlay && this.overlay.parentElement !== document.body) {
          document.body.appendChild(this.overlay);
        }

        this.isOpen = false;
        this.term = '';
        this.cache = new Map();
        this.controller = null;
        this.debounceTimer = null;
        this.hideTimer = null;
        this.onDialogEnd = null;
        this.previousFocus = null;
        this.bodyWasLocked = false;

        this.onTriggerClick = () => this.open();
        this.onCloseClick = () => this.close(true);
        this.onBackdropClick = () => this.close(true);
        this.onInput = () => this.handleInput();
        this.onSubmit = () => this.saveRecent(this.input?.value.trim());
        this.onClearClick = () => this.clearInput();
        this.onDocKeydown = (event) => this.handleKeydown(event);
        this.onBodyClick = (event) => this.handleBodyClick(event);
        this.onSectionUnload = (event) => {
          if (event.target === this.closest('.shopify-section')) this.close(false);
        };
        this.onRailPrev = () => this.scrollRail(-1);
        this.onRailNext = () => this.scrollRail(1);
        this.onRailScroll = () => this.updateRailControls();
        this.onViewportChange = () => this.updatePlaceholder();

        this.trigger?.addEventListener('click', this.onTriggerClick);
        this.closeButton?.addEventListener('click', this.onCloseClick);
        this.backdrop?.addEventListener('click', this.onBackdropClick);
        this.input?.addEventListener('input', this.onInput);
        this.form?.addEventListener('submit', this.onSubmit);
        this.clearButton?.addEventListener('click', this.onClearClick);
        this.body?.addEventListener('click', this.onBodyClick);
        this.railPrev?.addEventListener('click', this.onRailPrev);
        this.railNext?.addEventListener('click', this.onRailNext);
        this.rail?.addEventListener('scroll', this.onRailScroll, { passive: true });
        this.desktopQuery.addEventListener('change', this.onViewportChange);
        document.addEventListener('keydown', this.onDocKeydown);
        document.addEventListener('shopify:section:unload', this.onSectionUnload);

        if (this.rail && 'ResizeObserver' in window) {
          this.railResizeObserver = new ResizeObserver(() => this.updateRailControls());
          this.railResizeObserver.observe(this.rail);
        }

        this.renderRecent();
        this.toggleClear();
        this.updatePlaceholder();
        requestAnimationFrame(() => {
          this.overlay?.classList.add('csb--ready');
          this.updateRailControls();
        });
      }

      disconnectedCallback() {
        this.trigger?.removeEventListener('click', this.onTriggerClick);
        this.closeButton?.removeEventListener('click', this.onCloseClick);
        this.backdrop?.removeEventListener('click', this.onBackdropClick);
        this.input?.removeEventListener('input', this.onInput);
        this.form?.removeEventListener('submit', this.onSubmit);
        this.clearButton?.removeEventListener('click', this.onClearClick);
        this.body?.removeEventListener('click', this.onBodyClick);
        this.railPrev?.removeEventListener('click', this.onRailPrev);
        this.railNext?.removeEventListener('click', this.onRailNext);
        this.rail?.removeEventListener('scroll', this.onRailScroll);
        this.desktopQuery?.removeEventListener('change', this.onViewportChange);
        document.removeEventListener('keydown', this.onDocKeydown);
        document.removeEventListener('shopify:section:unload', this.onSectionUnload);
        this.railResizeObserver?.disconnect();
        this.cancelHide();
        window.clearTimeout(this.debounceTimer);
        this.controller?.abort();
        if (this.isOpen) {
          this.unlockScroll();
          document.dispatchEvent(new CustomEvent('cahve-search:close', { bubbles: true }));
        }
        // Remove the portaled overlay so a Theme Editor section reload does not
        // leave an orphan in <body>.
        this.overlay?.remove();
        this.isReady = false;
      }

      /* --------------------------------------------------------- open/close */
      open() {
        if (this.isOpen || !this.overlay || !this.dialog) return;
        // Re-assert the portal so the overlay is always a direct child of <body>
        // (and its dim layer covers the whole page) even if the initial move was
        // missed or the node was re-parented by the Theme Editor.
        if (this.overlay.parentElement !== document.body) {
          document.body.appendChild(this.overlay);
        }
        this.previousFocus = document.activeElement;
        this.isOpen = true;
        this.cancelHide();
        this.overlay.hidden = false;
        this.prepareOrigin();
        // Show the collapsed dialog, commit that frame, then expand so the
        // clip-path reveal animates from a painted state (reliable first open).
        this.overlay.classList.add('csb--visible');
        void this.dialog.offsetWidth;
        this.overlay.classList.add('csb--open');
        this.trigger?.setAttribute('aria-expanded', 'true');
        this.lockScroll();
        document.dispatchEvent(new CustomEvent('cahve-search:open', { bubbles: true }));
        window.requestAnimationFrame(() => {
          this.input?.focus();
          this.updateRailControls();
        });
      }

      close(restoreFocus) {
        if (!this.isOpen) return;
        this.isOpen = false;
        this.prepareOrigin();
        this.overlay.classList.remove('csb--open');
        this.trigger?.setAttribute('aria-expanded', 'false');
        this.unlockScroll();
        document.dispatchEvent(new CustomEvent('cahve-search:close', { bubbles: true }));
        if (restoreFocus) {
          const target = this.trigger || this.previousFocus;
          target?.focus?.();
        }
        this.scheduleHide();
      }

      scheduleHide() {
        this.cancelHide();
        const finish = () => this.finishHide();
        if (this.reducedMotion.matches) {
          finish();
          return;
        }
        this.onDialogEnd = (event) => {
          const closingProperty = this.desktopQuery.matches ? 'clip-path' : 'transform';
          if (event.target === this.dialog && event.propertyName === closingProperty) finish();
        };
        this.dialog.addEventListener('transitionend', this.onDialogEnd);
        this.hideTimer = window.setTimeout(finish, this.desktopQuery.matches ? 820 : 780);
      }

      cancelHide() {
        window.clearTimeout(this.hideTimer);
        this.hideTimer = null;
        if (this.onDialogEnd) this.dialog.removeEventListener('transitionend', this.onDialogEnd);
        this.onDialogEnd = null;
      }

      finishHide() {
        this.cancelHide();
        if (this.isOpen) return;
        this.overlay.classList.remove('csb--visible');
        if (this.overlay) this.overlay.hidden = true;
      }

      /* ------------------------------------------------------------ input */
      handleInput() {
        const term = this.input.value.trim();
        this.toggleClear();
        if (term === this.term) return;
        this.term = term;
        window.clearTimeout(this.debounceTimer);
        if (!term) {
          this.controller?.abort();
          this.showDiscovery();
          return;
        }
        this.debounceTimer = window.setTimeout(() => this.fetchResults(term), 240);
      }

      clearInput() {
        if (!this.input) return;
        this.input.value = '';
        this.term = '';
        this.toggleClear();
        this.showDiscovery();
        this.input.focus();
      }

      toggleClear() {
        if (this.clearButton) this.clearButton.hidden = !(this.input && this.input.value.trim().length);
      }

      updatePlaceholder() {
        if (!this.input) return;
        this.input.placeholder = this.desktopQuery.matches
          ? this.input.dataset.desktopPlaceholder || ''
          : this.input.dataset.mobilePlaceholder || this.input.dataset.desktopPlaceholder || '';
      }

      async fetchResults(term) {
        if (this.cache.has(term)) {
          if (this.input.value.trim() === term) this.renderResults(this.cache.get(term));
          return;
        }
        this.controller?.abort();
        this.controller = new AbortController();
        this.overlay.classList.add('csb--loading');
        try {
          const url = `${this.predictiveUrl}?q=${encodeURIComponent(term)}&section_id=${
            this.sectionId
          }&resources[type]=query,product,collection&resources[limit]=10`;
          const response = await fetch(url, { signal: this.controller.signal });
          if (!response.ok) throw new Error(String(response.status));
          const markup = this.extractResults(await response.text());
          this.cache.set(term, markup);
          if (this.input.value.trim() === term) this.renderResults(markup);
        } catch (error) {
          if (error.name !== 'AbortError') this.showDiscovery();
        } finally {
          this.overlay.classList.remove('csb--loading');
        }
      }

      extractResults(text) {
        const parsed = new DOMParser().parseFromString(text, 'text/html');
        const root = parsed.querySelector('[data-csb-results-root]');
        return root ? root.outerHTML : text;
      }

      renderResults(markup) {
        if (!this.results) return;
        this.results.innerHTML = markup;
        this.results.hidden = false;
        if (this.discovery) this.discovery.hidden = true;
        if (this.body) this.body.scrollTop = 0;
      }

      showDiscovery() {
        if (this.results) {
          this.results.hidden = true;
          this.results.innerHTML = '';
        }
        if (this.discovery) this.discovery.hidden = false;
      }

      /* ---------------------------------------------------- body delegation */
      handleBodyClick(event) {
        const tab = event.target.closest('[data-csb-tab]');
        if (tab) {
          event.preventDefault();
          this.activateTab(tab);
          return;
        }

        const recentButton = event.target.closest('[data-csb-recent-term]');
        if (recentButton && this.input) {
          this.input.value = recentButton.dataset.csbRecentTerm;
          this.input.dispatchEvent(new Event('input', { bubbles: true }));
          this.input.focus();
          return;
        }

        if (event.target.closest('a[href]')) {
          this.saveRecent(this.input?.value.trim());
        }
      }

      activateTab(tab) {
        const tabList = tab.closest('[data-csb-tabs]');
        const scope = tab.closest('.csb-results__main');
        if (!tabList || !scope) return;
        const name = tab.dataset.csbTab;
        tabList.querySelectorAll('[data-csb-tab]').forEach((button) => {
          const active = button === tab;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        scope.querySelectorAll('[data-csb-panel]').forEach((panel) => {
          panel.hidden = panel.dataset.csbPanel !== name;
        });
      }

      /* --------------------------------------------------------- keyboard */
      handleKeydown(event) {
        if (!this.isOpen) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close(true);
          return;
        }
        if (event.key === 'Tab') this.trapFocus(event);
      }

      trapFocus(event) {
        if (!this.dialog) return;
        const focusable = Array.from(
          this.dialog.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
          ),
        ).filter((element) => element.offsetParent !== null && !element.closest('[hidden]'));
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      /* ------------------------------------------------------ recent terms */
      getRecent() {
        try {
          const saved = JSON.parse(window.localStorage.getItem('cahve-recent-searches') || '[]');
          return Array.isArray(saved)
            ? saved.filter((term) => typeof term === 'string' && term.trim()).slice(0, 6)
            : [];
        } catch (_error) {
          return [];
        }
      }

      saveRecent(term) {
        if (!term) return;
        const list = this.getRecent().filter((item) => item.toLowerCase() !== term.toLowerCase());
        list.unshift(term);
        try {
          window.localStorage.setItem('cahve-recent-searches', JSON.stringify(list.slice(0, 6)));
        } catch (_error) {
          /* storage unavailable — recent searches simply won't persist */
        }
        this.renderRecent();
      }

      renderRecent() {
        if (!this.recent || !this.recentList) return;
        const list = this.getRecent();
        this.recent.hidden = list.length === 0;
        this.recentList.replaceChildren();
        list.forEach((term) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'csb__chip';
          button.dataset.csbRecentTerm = term;
          const text = document.createElement('span');
          text.className = 'csb__chip-text';
          const textLine = document.createElement('span');
          textLine.className = 'csb__chip-text-line';
          textLine.textContent = term;
          const textClone = document.createElement('span');
          textClone.className = 'csb__chip-text-line csb__chip-text-line--clone';
          textClone.setAttribute('aria-hidden', 'true');
          textClone.textContent = term;
          text.append(textLine, textClone);
          const arrow = document.createElement('span');
          arrow.className = 'csb__chip-arrow';
          arrow.setAttribute('aria-hidden', 'true');
          arrow.innerHTML =
            '<svg viewBox="0 0 7 7" fill="none"><path d="M0.48249 0.923833L0.482511 1.77624L4.82887 1.77626L0.625632 5.77029L1.25997 6.37305L5.4632 2.37902L5.4632 6.50903L6.36025 6.50905L6.36025 0.923812L0.48249 0.923833Z" fill="currentColor"></path></svg>';
          button.append(text, arrow);
          this.recentList.append(button);
        });
      }

      /* --------------------------------------------------------- animation */
      prepareOrigin() {
        if (!this.desktopQuery.matches || !this.trigger || !this.dialog) return;
        const triggerRect = this.trigger.getBoundingClientRect();
        const dialogRect = this.dialog.getBoundingClientRect();
        if (!dialogRect.width || !dialogRect.height) return;
        const radius = Math.max(Math.min(triggerRect.width, triggerRect.height), 44) / 2;
        const originX = triggerRect.left + triggerRect.width / 2 - dialogRect.left;
        const originY = triggerRect.top + triggerRect.height / 2 - dialogRect.top;
        const clamp = (value, max) => Math.max(Math.min(value, max), 0);
        this.dialog.style.setProperty('--csb-origin-top', `${clamp(originY - radius, dialogRect.height)}px`);
        this.dialog.style.setProperty('--csb-origin-right', `${clamp(dialogRect.width - originX - radius, dialogRect.width)}px`);
        this.dialog.style.setProperty('--csb-origin-bottom', `${clamp(dialogRect.height - originY - radius, dialogRect.height)}px`);
        this.dialog.style.setProperty('--csb-origin-left', `${clamp(originX - radius, dialogRect.width)}px`);
        this.dialog.style.setProperty('--csb-origin-x', `${originX}px`);
        this.dialog.style.setProperty('--csb-origin-y', `${originY}px`);
      }

      /* ------------------------------------------------------ rail scroll */
      scrollRail(direction) {
        if (!this.rail) return;
        const metrics = this.getRailMetrics();
        if (!metrics || metrics.maximumIndex === 0) return;
        const targetIndex = Math.min(
          Math.max(metrics.currentIndex + metrics.visibleCards * direction, 0),
          metrics.maximumIndex,
        );
        const firstRect = metrics.cards[0].getBoundingClientRect();
        const targetRect = metrics.cards[targetIndex].getBoundingClientRect();
        const target = targetRect.left - firstRect.left;
        this.rail.scrollTo({
          left: target,
          behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
        });
      }

      getRailMetrics() {
        if (!this.rail) return null;
        const cards = Array.from(this.rail.querySelectorAll('.csb__card'));
        if (!cards.length) return null;
        const styles = window.getComputedStyle(this.rail);
        const gap = Number.parseFloat(styles.columnGap) || 0;
        const cardWidth = cards[0].getBoundingClientRect().width;
        if (!cardWidth) return null;
        const step = cardWidth + gap;
        const visibleCards = Math.max(1, Math.floor((this.rail.clientWidth + gap) / step));
        const maximumIndex = Math.max(cards.length - visibleCards, 0);
        const currentIndex = Math.min(
          Math.max(Math.round(this.rail.scrollLeft / step), 0),
          maximumIndex,
        );
        return { cards, visibleCards, maximumIndex, currentIndex };
      }

      updateRailControls() {
        const metrics = this.getRailMetrics();
        if (!metrics) {
          if (this.railPrev) this.railPrev.disabled = true;
          if (this.railNext) this.railNext.disabled = true;
          return;
        }
        if (this.railPrev) this.railPrev.disabled = metrics.currentIndex === 0;
        if (this.railNext) this.railNext.disabled = metrics.currentIndex >= metrics.maximumIndex;
      }

      /* ------------------------------------------------------- scroll lock */
      lockScroll() {
        this.bodyWasLocked = document.body.classList.contains('overflow-hidden');
        document.body.classList.add('overflow-hidden');
      }

      unlockScroll() {
        if (!this.bodyWasLocked) document.body.classList.remove('overflow-hidden');
      }
    },
  );
}
